import * as React from 'react';

import { t } from '~/i18n';
import { Spinner, alertColors, formatDateTime } from '@ixicare/ui';
import { translateRejectReason, translateResolveReason } from '~/config/alert';
import { observer } from 'mobx-react';
import Alert from '~store/Alert/Alert';
import { AlertAction, Device, AlertUpdateEvent, ProximityResolveAction } from '@ixicare/resources';
import { useQuery } from '@tanstack/react-query';
import { useStores } from '~store/utils';
import { useHcpGroups } from '~hooks/useHcpGroups';
import AlertEventIcon, { ALERT_EVENT_COLORS, alertActionEventKind } from './AlertEventIcon';
import styles from './AlertLogs.module.css';

interface AlertLogsProps {
  alert: Alert;
  shortVersion?: boolean;
}
const AlertLogs: React.FC<AlertLogsProps> = observer(({ alert, shortVersion }) => {
  const { alerts } = useStores();
  const siteUuid = alert.siteUuid ?? undefined;

  React.useEffect(() => {
    alerts.fetchAlert(alert?.uuid);
  }, [alert?.uuid]);

  if (shortVersion) {
    if (alert.isCancelled) {
      return <p>{t('alarm.proximityResolveAction.cancelled.limited.description')}</p>;
    }
    if (alert.resolvedProximityAction == null) {
      //TODO: Potentially auto resolved
      return <p>-</p>;
    }
    return (
      <p>{t(`alarm.proximityResolveAction.${alert.resolvedProximityAction.toLowerCase()}.limited.description`)}</p>
    );
  } else {
    return (
      <ul className={styles.Logs}>
        <li>
          <AlertEventIcon kind="triggered" color={alertColors[alert.priority]} />
          {t('alert.logs.types.create', {
            datetime: formatDateTime(alert.created),
          })}
        </li>
        {alert.alertUpdateEvents
          ?.slice()
          .sort((a, b) => {
            return a.created.getTime() - b.created.getTime();
          })
          .map((event) => (
            <AlertUpdateEventItem key={event.id} event={event} siteUuid={siteUuid} />
          ))}
        {alert.isCancelled && (
          <li>
            <AlertEventIcon kind="cancelled" color={ALERT_EVENT_COLORS.cancelled} />
            {t('alert.logs.types.cancel', {
              datetime: formatDateTime(alert.cancelledTime),
            })}
          </li>
        )}
      </ul>
    );
  }
});

export function AlertUpdateEventItem({ event, siteUuid }: { event: AlertUpdateEvent; siteUuid?: string }) {
  if (!event) {
    return <Spinner label={t('global.loading')} />;
  }

  const { data: device } = useQuery({
    queryKey: ['device', event.proximityDeviceId],
    queryFn: () => Device.getDevice(event.proximityDeviceId ?? undefined),
    enabled: !!event.proximityDeviceId,
  });

  const { data: hcpGroups } = useHcpGroups(siteUuid, { staleTime: 5 * 60 * 1000 });

  const isSystemActor = (event.impersonatorProfile ?? event.profile)?.isSystem === true;
  const kind = alertActionEventKind(event.action);
  const icon = <AlertEventIcon kind={kind} color={ALERT_EVENT_COLORS[kind]} />;
  if (event.action === AlertAction.Resolve) {
    return (
      <>
        <li key={event.id}>
          {icon}
          {alertUpdateEventText(event, hcpGroups)}
        </li>
        {event.proximityResolveAction ? (
          <li key={event.id + 'prox'}>
            {event.proximityResolveAction === ProximityResolveAction.DASHBOARD ? (
              <AlertEventIcon kind="resolved" color={ALERT_EVENT_COLORS.resolved} />
            ) : (
              <AlertEventIcon />
            )}
            <p>
              {t(`alarm.proximityResolveAction.${event.proximityResolveAction.toLowerCase()}.description`)}
              <strong>{device ? ` ${device?.macAddress ?? '-'} ${device?.description ?? '-'}` : ''}</strong>
            </p>
          </li>
        ) : (
          // A system resolve already reads as automatic, so it gets no follow-up line.
          !isSystemActor && (
            <li key={event.id + 'prox'}>
              <AlertEventIcon kind="resolved" color={ALERT_EVENT_COLORS.resolved} />
              <p>{t(`alarm.proximityResolveAction.on.pc`)}</p>
            </li>
          )
        )}
      </>
    );
  }
  return (
    <li key={event.id}>
      {icon}
      {alertUpdateEventText(event, hcpGroups)}
    </li>
  );
}

/** The log line for an action taken on an alarm, as shown in the alarm logs. */
export function alertUpdateEventText(event: AlertUpdateEvent, hcpGroups?: { id: string; hcpGroupName: string }[]) {
  const actor = event.impersonatorProfile ?? event.profile;
  const isSystemActor = actor?.isSystem === true;
  let name = isSystemActor ? t('alert.systemActor') : actor ? `${actor.lastName.toUpperCase()} ${actor.firstName}` : '';
  if (event.impersonatorProfile) {
    name += ` (${t('impersonation.banner.title', {
      name: `${event.profile.lastName.toUpperCase()} ${event.profile.firstName}`,
    })})`;
  }
  if (event.hcpGroupId) {
    const hcpGroupName = hcpGroups?.find((g) => g.id === event.hcpGroupId)?.hcpGroupName ?? event.hcpGroupId;
    name += ` (${hcpGroupName})`;
  }
  const reason = getActionReason(event);
  return t(logKey(event.action, isSystemActor, reason), {
    datetime: formatDateTime(event.created),
    reason: reason,
    name: name,
  });
}

/** What the platform does is phrased as automatic; only an action we have no system wording for
 *  falls back to the regular line, where the actor then reads as the system. */
function logKey(action: AlertAction, isSystemActor: boolean, reason?: string) {
  if (isSystemActor && action === AlertAction.Accept) {
    return 'alert.logs.types.system.accept';
  }
  if (isSystemActor && action === AlertAction.Resolve) {
    return reason ? 'alert.logs.types.system.resolve' : 'alert.logs.types.system.resolveNoReason';
  }
  return `alert.logs.types.${action.toLowerCase()}`;
}

function getActionReason(event: AlertUpdateEvent) {
  if (!event.reason) return undefined;
  if (event.action === AlertAction.Resolve) return translateResolveReason(event.reason) ?? '-';
  if (event.action === AlertAction.Reject) return translateRejectReason(event.reason) ?? '-';
  return undefined;
}

export default AlertLogs;
